import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Flame } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { HourlyHeatmap, HourlyHeatmapPoint } from '@/types/teamPerformance'

interface Props {
  heatmap: HourlyHeatmap
  darkMode: boolean
}

// 5 paliers d'intensité. Tailwind ne peut pas générer dynamiquement les classes,
// donc on liste toutes les combinaisons à l'avance. Paliers basés sur ratio
// count / max → 0% / 20% / 40% / 60% / 80% / 100%.
const INTENSITY_CLASSES = {
  all: {
    light: [
      'bg-slate-100',
      'bg-indigo-100',
      'bg-indigo-200',
      'bg-indigo-300',
      'bg-indigo-400',
      'bg-indigo-500',
    ],
    dark: [
      'bg-slate-800',
      'bg-indigo-900/60',
      'bg-indigo-800/70',
      'bg-indigo-700/80',
      'bg-indigo-600/90',
      'bg-indigo-500',
    ],
  },
  missed: {
    light: [
      'bg-slate-100',
      'bg-rose-100',
      'bg-rose-200',
      'bg-rose-300',
      'bg-rose-400',
      'bg-rose-500',
    ],
    dark: [
      'bg-slate-800',
      'bg-rose-900/60',
      'bg-rose-800/70',
      'bg-rose-700/80',
      'bg-rose-600/90',
      'bg-rose-500',
    ],
  },
}

// Heures affichées dans le header (sinon trop dense). On garde toutes les
// colonnes pour l'alignement mais on n'écrit que toutes les 3 heures.
const HEADER_HOURS = [0, 3, 6, 9, 12, 15, 18, 21]

type Mode = 'all' | 'missed'

export function TeamHeatmap({ heatmap, darkMode }: Props) {
  const { t } = useTranslation()
  const [mode, setMode] = useState<Mode>('all')

  const data = mode === 'all' ? heatmap.hourly_all : heatmap.hourly_missed

  // Index direct pour O(1) lookup par cellule.
  const { bucketsByCell, max, peak } = useMemo(() => {
    const map = new Map<string, number>()
    let m = 0
    let pk: HourlyHeatmapPoint | null = null
    for (const p of data) {
      map.set(`${p.day}-${p.hour}`, p.count)
      if (p.count > m) {
        m = p.count
        pk = p
      }
    }
    return { bucketsByCell: map, max: m, peak: pk }
  }, [data])

  const palette = INTENSITY_CLASSES[mode][darkMode ? 'dark' : 'light']
  const intensityFor = (count: number): string => {
    if (count === 0 || max === 0) return palette[0]
    const ratio = count / max
    if (ratio < 0.2) return palette[1]
    if (ratio < 0.4) return palette[2]
    if (ratio < 0.6) return palette[3]
    if (ratio < 0.8) return palette[4]
    return palette[5]
  }

  const days = [0, 1, 2, 3, 4, 5, 6]
  const hours = Array.from({ length: 24 }, (_, i) => i)

  const titleColor = darkMode ? 'text-white' : 'text-slate-900'
  const subtitleColor = darkMode ? 'text-slate-400' : 'text-slate-500'
  const labelColor = darkMode ? 'text-slate-500' : 'text-slate-400'
  const cardBg = darkMode ? 'bg-slate-800/40 border-slate-700' : 'bg-white border-slate-200'

  return (
    <div className={cn('rounded-xl border p-5', cardBg)}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h3 className={cn('font-semibold flex items-center gap-2', titleColor)}>
            <Flame className="h-4 w-4 text-amber-500" />
            {t('manager.analytics.heatmap.title')}
          </h3>
          <p className={cn('text-xs mt-0.5', subtitleColor)}>
            {peak
              ? t('manager.analytics.heatmap.peakLine', {
                  day: t(`manager.analytics.heatmap.days.${peak.day}`),
                  hour: peak.hour,
                  count: peak.count,
                })
              : t('manager.analytics.heatmap.noPeak')}
            {' · '}
            {t('manager.analytics.heatmap.tzLine', { tz: heatmap.timezone })}
          </p>
        </div>
        <ModeToggle mode={mode} onChange={setMode} darkMode={darkMode} />
      </div>

      {/* Grille */}
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full">
          {/* Header heures */}
          <div className="flex items-end mb-1">
            <div className="w-10 flex-shrink-0" />
            <div
              className="flex-1 grid gap-0.5"
              style={{ gridTemplateColumns: 'repeat(24, minmax(0, 1fr))' }}
            >
              {hours.map((h) => (
                <div
                  key={h}
                  className={cn(
                    'text-[10px] text-center tabular-nums',
                    labelColor,
                  )}
                >
                  {HEADER_HOURS.includes(h) ? `${h}h` : ''}
                </div>
              ))}
            </div>
          </div>

          {/* Lignes de jours */}
          {days.map((d) => (
            <div key={d} className="flex items-center mb-0.5">
              <div
                className={cn(
                  'w-10 flex-shrink-0 text-xs font-medium tabular-nums',
                  subtitleColor,
                )}
              >
                {t(`manager.analytics.heatmap.days.${d}`)}
              </div>
              <div
              className="flex-1 grid gap-0.5"
              style={{ gridTemplateColumns: 'repeat(24, minmax(0, 1fr))' }}
            >
                {hours.map((h) => {
                  const count = bucketsByCell.get(`${d}-${h}`) || 0
                  return (
                    <div
                      key={h}
                      className={cn(
                        'aspect-square rounded-sm transition-transform',
                        intensityFor(count),
                        count > 0 && 'hover:scale-110 cursor-help',
                      )}
                      title={
                        count > 0
                          ? t('manager.analytics.heatmap.cellTooltip', {
                              day: t(`manager.analytics.heatmap.days.${d}`),
                              hour: h,
                              count,
                            })
                          : ''
                      }
                    />
                  )
                })}
              </div>
            </div>
          ))}

          {/* Légende d'intensité */}
          <div className="flex items-center justify-end gap-2 mt-3">
            <span className={cn('text-[10px]', labelColor)}>
              {t('manager.analytics.heatmap.scaleLow')}
            </span>
            <div className="flex gap-0.5">
              {palette.slice(1).map((cls, i) => (
                <div key={i} className={cn('w-4 h-3 rounded-sm', cls)} />
              ))}
            </div>
            <span className={cn('text-[10px]', labelColor)}>
              {t('manager.analytics.heatmap.scaleHigh')}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

function ModeToggle({
  mode,
  onChange,
  darkMode,
}: {
  mode: Mode
  onChange: (m: Mode) => void
  darkMode: boolean
}) {
  const { t } = useTranslation()
  const baseBtn = cn(
    'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
    'focus:outline-none focus:ring-2 focus:ring-indigo-500/40',
  )
  const active = darkMode
    ? 'bg-slate-700 text-white shadow-sm'
    : 'bg-white text-slate-900 shadow-sm'
  const inactive = darkMode
    ? 'text-slate-400 hover:text-slate-200'
    : 'text-slate-500 hover:text-slate-700'

  return (
    <div
      className={cn(
        'inline-flex rounded-lg p-1',
        darkMode ? 'bg-slate-700/50' : 'bg-slate-100',
      )}
    >
      <button
        type="button"
        className={cn(baseBtn, mode === 'all' ? active : inactive)}
        onClick={() => onChange('all')}
      >
        {t('manager.analytics.heatmap.modeAll')}
      </button>
      <button
        type="button"
        className={cn(baseBtn, mode === 'missed' ? active : inactive)}
        onClick={() => onChange('missed')}
      >
        {t('manager.analytics.heatmap.modeMissed')}
      </button>
    </div>
  )
}
